import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Spin, Tooltip, Table, message } from 'antd';
import { 
    DownloadSimple, FileText, PencilSimple, PlusCircle, MinusCircle, 
    Article, CheckCircle, XCircle, ArrowsClockwise, ArrowSquareOut, EnvelopeSimple,
    CaretDown, CaretUp, UserCircle
} from '@phosphor-icons/react';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import contractService from '../../../../services/oneButtonServices/contractService';
import { contractApiErrorMessage, roofingProposalUrlFromApi } from './contractFlowHelpers';
import { TrinityProposalModal, getViewTrinityProposalModalTitle } from './TrinityProposalModal';
import DashboardModal from './DashboardModal';
import { useOneButtonStore } from '../../../../store/oneButtonStore';

import './oneButton-css/ViewTrinityContractModal.css';

/** Above View Trinity modal (`zIndex={11000}`) and Agreements (`~10060`); nested static modals default too low otherwise. */
const NESTED_MODAL_Z_INDEX = 11060;
/** Controlled duplicate-email dialog above View Trinity (`11000`) + nested history (`11060`). */
const DUPLICATE_IN_PERSON_MODAL_Z = 11100;
/** Signing-conflict + duplicate follow-ups — above View Trinity (`11000`) / duplicate (`11100`). */
const SIGNING_FLOW_OVERLAY_Z = 11120;

/** Angular `view-trinity-contract.component.ts` — duplicate-email `nzContent` (`nzTitle` empty). */
const DUPLICATE_IN_PERSON_EMAIL_BODY =
    'There are multiple contacts listed with the same email address. If you click "OK" and proceed with In-Person Signing, only the Primary Contact will be able to sign the Combined Pack.\nTo ensure all signees can sign as needed, please click on "Cancel" and use the Email Signing option instead.';

/** Angular `masterService.opportunityId` — React receives prop + optional nested ids from opportunity payload. */
function resolveTrinityOpportunityIdForApis(
    opportunityIdProp: string | number | null | undefined,
    opportunityDetails: unknown,
): string | number | null {
    if (opportunityIdProp != null && String(opportunityIdProp).trim() !== '') return opportunityIdProp;
    const o = opportunityDetails as Record<string, unknown> | null | undefined;
    if (!o) return null;
    const ids = [o.Opportunity_Id, o.OpportunityId, o.opportunity_Id, o.opportunityId];
    for (const id of ids) {
        if (id != null && String(id).trim() !== '') return id as string | number;
    }
    return null;
}

function normalizeSigningRecordsList(data: unknown): any[] {
    if (Array.isArray(data)) return data;
    if (data != null && typeof data === 'object') {
        const o = data as Record<string, unknown>;
        const inner = o.data ?? o.Data ?? o.records ?? o.Records ?? o.result ?? o.Result;
        if (Array.isArray(inner)) return inner;
    }
    return [];
}

/** Angular `if (res)` on `CheckDuplicateEmails` — avoid treating JSON string `"false"` as duplicate. */
function isCheckDuplicateEmailsTruthyLikeAngular(data: unknown): boolean {
    if (data === true) return true;
    if (data === false || data == null) return false;
    if (typeof data === 'string') {
        const raw = data.trim();
        if (!raw) return false;
        const s = raw.toLowerCase();
        if (s === 'false' || s === '0' || s === 'no' || s === 'null' || s === 'undefined') return false;
        if (s === 'true' || s === '1' || s === 'yes') return true;
        return true;
    }
    if (typeof data === 'number') return data !== 0 && !Number.isNaN(data);
    if (typeof data === 'object' && !Array.isArray(data)) {
        const o = data as Record<string, unknown>;
        if (o.hasDuplicate === false || o.HasDuplicate === false) return false;
        if (o.duplicate === false || o.Duplicate === false) return false;
        if (o.IsSuccessful === false || o.isSuccessful === false) return false;
        const inner =
            o.hasDuplicate ??
            o.HasDuplicate ??
            o.duplicate ??
            o.Duplicate ??
            o.Result ??
            o.result ??
            o.Value ??
            o.value ??
            o.Data ??
            o.data;
        if (inner !== undefined && inner !== data) {
            return isCheckDuplicateEmailsTruthyLikeAngular(inner);
        }
        return Object.keys(o).length > 0;
    }
    return Boolean(data);
}

function unwrapAgreementIdFromViewTrinityResponse(res: unknown): string | null {
    if (res == null) return null;
    if (typeof res === 'object' && !Array.isArray(res)) {
        const o = res as Record<string, unknown>;
        const direct = o.agreementId ?? o.Agreement_Id ?? o.AgreementId;
        if (direct != null && String(direct).trim() !== '') return String(direct);
        const inner = (o.data ?? o.Data) as Record<string, unknown> | undefined;
        if (inner != null && typeof inner === 'object') {
            const id = inner.agreementId ?? inner.Agreement_Id ?? inner.AgreementId;
            if (id != null && String(id).trim() !== '') return String(id);
        }
    }
    return null;
}

const DOCUMENTS_CONTROLS = [
    { name: 'CombinedPack', label: 'Combined Pack', value:'Combined Pack', isDefaultChecked:true, hidden:false },
    { name: 'DesignIntent', label: 'Design Intent',value: 'Design Intent', isDefaultChecked:false , hidden:true},
    { name: 'LoadCalculator', label: 'Load Calculator',value:'Load Calculator', isDefaultChecked:false, hidden:false},
    { name: 'ConsumptionApprovalLetterandMOU', label: 'Consumption Approval Letter and MOU', value:'Consumption Approval Letter', isDefaultChecked:false, hidden:false},
    { name: 'RoofDisclosureForm', label: 'Roof Disclosure Form',value:'Roof Disclosure Form', isDefaultChecked:false, hidden:true},
    { name: 'RhodeIslandREFAddendum', label: 'Rhode Island REF Addendum',value:'Rhode Island REF Addendum', isDefaultChecked:false, hidden:true},
    { name: 'AllTreeChecklist', label: 'All Tree Checklist',value:'All Tree Checklist', isDefaultChecked:false, hidden:false},
    { name: 'SolarCashContractAddendum', label: 'Solar Cash Contract Addendum', value: 'Solar Cash Contract Addendum', isDefaultChecked: false, hidden: true },
    { name: 'BatteryCashContractAddendum', label: 'Battery Cash Contract Addendum', value: 'Battery Cash Contract Addendum', isDefaultChecked: false, hidden: true },
];

/** Minimal row fields for Trinity Combined Pack modal (matches `AgreementRow` from AgreementsModal). */
export type ViewTrinityContractRow = {
    key: string;
    quoteIdRaw: string;
    agreementIdRaw?: string;
    partnerFileUrl?: string | null;
    productTypeCode?: string;
    productType: string;
    financePartner: string;
    financePartnerRaw: string;
    financeMethod: string;
    loadCalculatorDataExists?: boolean;
    /** Trinity `ManualOverride` — `GenerateProposal` query param. */
    manualOverride?: boolean;
};

type DocControl = (typeof DOCUMENTS_CONTROLS)[number];

function normPartner(s: string) {
    return String(s || '')
        .replace(/\s/g, '')
        .toLowerCase();
}

/** Angular `ViewTrinityContractComponent`: RI addendum when `OpportunityDetails.State` is Rhode Island — also accept API/store shape variants. */
function isRhodeIslandOpportunity(opp: Record<string, unknown> | null | undefined): boolean {
    if (!opp) return false;
    const od = (opp.OpportunityDetails ?? opp.opportunityDetails) as Record<string, unknown> | undefined;
    const candidates: string[] = [];
    const push = (v: unknown) => {
        if (v == null) return;
        const s = String(v).trim();
        if (s) candidates.push(s);
    };
    if (od) {
        push(od.State);
        push(od.state);
    }
    const inst = (opp.InstallationAddress ?? opp.installationAddress) as Record<string, unknown> | undefined;
    if (inst) {
        push(inst.stateCode);
        push(inst.StateCode);
        push(inst.State);
        push(inst.state);
    }
    push(opp.InstallationState);
    push(opp.installationState);

    return candidates.some((s) => {
        const n = s.toLowerCase();
        return n === 'rhode island' || n.includes('rhode island') || n === 'ri' || n === 'r.i.';
    });
}

function applyDocumentVisibility(
    controls: DocControl[],
    row: ViewTrinityContractRow,
    opportunityDetails: unknown,
    loadCalculatorExists: boolean | undefined,
): DocControl[] {
    const opp = opportunityDetails as Record<string, unknown> | null | undefined;
    const oi = (opp?.OpportunityInterest ?? opp?.opportunityInterest) as Record<string, unknown> | undefined;
    const roof = !!(oi?.Roof || oi?.RRR || oi?.roof || oi?.rrr);
    const ri = isRhodeIslandOpportunity(opp);
    const pt = String(row.productTypeCode || row.productType || '').trim();
    const fp = row.financePartnerRaw || row.financePartner || '';
    const fm = String(row.financeMethod || '');
    const fpN = normPartner(fp);
    const goodLeap = fpN.includes('good') && fpN.includes('leap');
    // console.log('row:', row);    //OBA-5345

    const hideSolarCash = !(
        (pt === 'S' || pt === 'SB') &&
        (fp === 'Cash' ||
            fp === 'Sunlight' ||
            fp === 'Service Finance' ||
            (goodLeap && (fm === 'Loan' || fm === 'Loan Storage')))
    );
    const hideBatteryCash = !(
        (pt === 'B' || pt === 'SB' || pt === 'BO') &&
        (fp === 'Cash' || (goodLeap && (fm === 'Battery Loan' || fm === 'Loan Storage')))
    );

    return controls.map((c) => {
        const next = { ...c };
        switch (c.name) {
            case 'LoadCalculator':
                next.hidden = !loadCalculatorExists;
                break;
            case 'RoofDisclosureForm':
                next.hidden = !roof;
                break;
            case 'RhodeIslandREFAddendum':
                next.hidden = !ri;
                break;
            case 'SolarCashContractAddendum':
                next.hidden = hideSolarCash;
                break;
            case 'BatteryCashContractAddendum':
                next.hidden = hideBatteryCash;
                break;
            default:
                break;
        }
        return next;
    });
}

export type ViewTrinityContractModalProps = {
    open: boolean;
    onClose: () => void;
    row: ViewTrinityContractRow | null;
    /** `proposal` — Angular `ViewRoofProposalComponent` + `SunnovaTrinityContractModelComponent` (width widened vs 518px for layout; no footer). */
    mode?: 'contract' | 'proposal';
    /** Matches Agreements modal — drives `obd-sync-modal-root` background (OneButtonUxRefresh). */
    theme?: 'dark' | 'light';
    salesforceId: string | null | undefined;
    opportunityId: string | number | null | undefined;
    opportunityDetails: unknown;
    isGenerate?: boolean;
    onAfterAction?: () => Promise<void>;
};

type PackTemplate = { label: string; checked: boolean; disabled: boolean };

/** Angular `GetAgreementDetails` + `moment(LastEventDate).format(...)` — email signing row timestamps. */
function formatLastEventDateLikeAngular(raw: unknown): string | null {
    if (raw == null || raw === '') return null;
    const d = new Date(String(raw));
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });
}

/** Angular `ViewTrinityContractComponent.GetAgreementDetails` — attach `LastEventDate` to qualifying signer rows. */
async function mergeAgreementLastEventIntoSigningRows(agreementId: string, rows: any[]): Promise<any[]> {
    try {
        const result = (await contractService.getAgreementDetails(agreementId)) as Record<string, unknown> | null;
        const lastRaw = result?.LastEventDate ?? result?.lastEventDate;
        const formatted = formatLastEventDateLikeAngular(lastRaw);
        return rows.map((signingDetail: any) => {
            const st = String(signingDetail.Signer_Status ?? '').toLowerCase();
            if (st === 'waiting_for_my_signature' || st === 'sent') {
                return { ...signingDetail, LastEventDate: formatted };
            }
            return { ...signingDetail, LastEventDate: null };
        });
    } catch {
        return rows;
    }
}

export function ViewTrinityContractModal({
    open,
    onClose,
    row,
    mode = 'contract',
    theme = 'dark',
    salesforceId,
    opportunityId,
    opportunityDetails,
    isGenerate,
    onAfterAction,
}: ViewTrinityContractModalProps) {
    const [checks, setChecks] = useState<Record<string, boolean>>({});
    const [controls, setControls] = useState<DocControl[]>([...DOCUMENTS_CONTROLS]);
    const [loading, setLoading] = useState(false);
    const [documentsUrl, setDocumentsUrl] = useState('');
    const [combinedExpanded, setCombinedExpanded] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [packTemplates, setPackTemplates] = useState<PackTemplate[]>([]);
    const [signingDetails, setSigningDetails] = useState<any[]>([]);
    const [signType, setSignType] = useState<'inperson' | 'email' | ''>('');
    const [isSigned, setIsSigned] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyData, setHistoryData] = useState<any>(null);
    const [duplicateInPersonOpen, setDuplicateInPersonOpen] = useState(false);
    /** Angular Swal after `GetSigningRecords` when `result.length > 0` — controlled `Modal` (static `Modal.confirm` stacks poorly). */
    const [signingConflict, setSigningConflict] = useState<{
        signingCompleted: boolean;
        body: string;
        method: 'inperson' | 'email';
    } | null>(null);
    const signingConflictResolveRef = useRef<(() => void) | null>(null);
    const signingConflictPendingRef = useRef<{ method: 'inperson' | 'email' } | null>(null);

    const modalBusy = loading || templatesLoading;

    const sf = String(salesforceId || '').trim();

    const handleClose = useCallback(() => {
        const oppId = resolveTrinityOpportunityIdForApis(opportunityId, opportunityDetails);
        if (oppId) {
            quoteServices.getSyncStatusList(Number(oppId)).then((res) => {
                if (res) {
                    const store = useOneButtonStore.getState();
                    if (res.FinancePartnerSyncStatuses) {
                        store.setFinancePartnerRawSyncStatuses(res.FinancePartnerSyncStatuses);
                    }
                    if (res.ContractCreationStatuses) {
                        store.setContractCreationStatuses(res.ContractCreationStatuses);
                    }
                }
            }).catch(() => {});
        }
        onClose();
    }, [onClose, opportunityId, opportunityDetails]);

    const loadSigningDetails = useCallback(async (agreementId: string) => {
        try {
            const res = await quoteServices.getAgreementSigningDetails(agreementId);
            const arr = Array.isArray(res) ? res : [];
            if (arr.length === 0) return;
            arr.sort((a: any, b: any) => (a.Signer_Order ?? 0) - (b.Signer_Order ?? 0));
            const sm0 = String(arr[0]?.Signing_Method ?? '');
            const inPersonFlow = sm0 === 'InPerson' || sm0.toLowerCase() === 'inperson';

            let allSigned = true;
            let loaderPlaced = false;
            let detailRows = arr.map((sign: any, index: number) => {
                if (!sign.Is_Signed) allSigned = false;
                let reloader = false;
                if (!sign.Signer_URL && !sign.Is_Signed && !loaderPlaced) {
                    reloader = true;
                    loaderPlaced = true;
                }
                if (index === arr.length - 1 && !allSigned && !loaderPlaced) {
                    reloader = true;
                }
                return { ...sign, reloader };
            });

            if (!inPersonFlow) {
                detailRows = await mergeAgreementLastEventIntoSigningRows(agreementId, detailRows);
            }

            setSigningDetails(detailRows);
            setIsSigned(allSigned);
            setSignType(inPersonFlow ? 'inperson' : 'email');
        } catch {
            /* ignore */
        }
    }, []);

    /** Angular `ViewTrinityContractComponent.checkEmailBox` — notify on email-signer row click. */
    const checkEmailBox = useCallback((email: unknown, signed: boolean) => {
        const e = email != null && String(email).trim() !== '' ? String(email).trim() : 'the signee';
        if (signed) message.success('This document has already been signed');
        else message.info(`Please check the mail box of ${e}, there will be pending sign document`);
    }, []);

    useEffect(() => {
        if (!open || !row) return;
        const vis = applyDocumentVisibility(
            [...DOCUMENTS_CONTROLS],
            row,
            opportunityDetails,
            row.loadCalculatorDataExists,
        );
        setControls(vis);
        const init: Record<string, boolean> = {};
        for (const c of vis) {
            init[c.name] = c.name === 'CombinedPack' ? true : !!c.isDefaultChecked;
        }
        setChecks(init);
        setDocumentsUrl(row.partnerFileUrl || '');
        setCombinedExpanded(false);
        setPackTemplates([]);
        setSigningDetails([]);
        setSignType('');
        setIsSigned(false);
        if (row.agreementIdRaw) {
            void loadSigningDetails(row.agreementIdRaw);
        }
    }, [open, row, opportunityDetails, loadSigningDetails]);

    
    useEffect(() => {
        if (!open) {
            setDuplicateInPersonOpen(false);
            setSigningConflict(null);
            signingConflictPendingRef.current = null;
            if (signingConflictResolveRef.current) {
                signingConflictResolveRef.current();
                signingConflictResolveRef.current = null;
            }
        }
    }, [open]);

    const visibleControls = useMemo(() => controls.filter((c) => !c.hidden), [controls]);

    const selectedDocumentValues = useCallback(() => {
        return controls
            .filter((c) => checks[c.name] && c.label !== 'Combined Pack')
            .map((c) => c.value);
    }, [controls, checks]);

    const selectedTemplateLabels = useCallback(() => {
        if (!combinedExpanded || packTemplates.length === 0) return [] as string[];
        return packTemplates.filter((d) => d.checked && !d.disabled).map((d) => d.label);
    }, [combinedExpanded, packTemplates]);

    const callViewTrinityContract = async (
        signingMethod: '' | 'inperson' | 'email',
    ): Promise<{ url?: string; agreementId?: string } | null> => {
        if (!sf || !row?.quoteIdRaw) {
            message.warning('Need Salesforce id and quote id.');
            return null;
        }
        if (signingMethod !== '') {
            setSignType(signingMethod);
        }
        /* Angular `getTrinityContract` — clears prior Combined Pack link before each contract call. */
        setDocumentsUrl('');
        const payload: Parameters<typeof quoteServices.viewTrinityContract>[0] = {
            salesforceId: sf,
            financePartnerQuoteId: row.quoteIdRaw,
            documentType: selectedDocumentValues(),
            selectedTemplate: selectedTemplateLabels(),
        };
        if (signingMethod !== '') {
            payload.isEmail = signingMethod === 'email';
            payload.signingMethod = signingMethod;
        }
        const res = await quoteServices.viewTrinityContract(payload);
        if (res != null && typeof res === 'string' && res.includes('Error:')) {
            message.error(res);
            return null;
        }
        if (signingMethod === '') {
            const url =
                roofingProposalUrlFromApi(res) ||
                (typeof res === 'string' && /^https?:\/\//i.test(res.trim()) ? res.trim() : null);
            if (url) {
                setDocumentsUrl(url);
                window.open(url, '_blank', 'noopener,noreferrer');
                await onAfterAction?.();
                return { url };
            }
            message.info('Contract view did not return a link.');
            return null;
        }
        const aid = unwrapAgreementIdFromViewTrinityResponse(res);
        if (aid) {
            await loadSigningDetails(aid);
            await onAfterAction?.();
            return { agreementId: aid };
        }
        message.error('Something went wrong, please try again.');
        return null;
    };

    const onDownload = async () => {
        setLoading(true);
        try {
            await callViewTrinityContract('');
        } catch (e: unknown) {
            message.error(contractApiErrorMessage(e) ?? (e instanceof Error ? e.message : 'Download failed.'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Angular `GetSigningRecords` → Swal (existing rows) or `getTrinityContract`; GET error: silent return.
     * Controlled signing-conflict `Modal` replaces `Modal.confirm` so it always appears above View Trinity / duplicate modals.
     * Do not set `loading` until user chooses **Yes** and `ViewTrinityContract` runs (or empty list → contract directly).
     */
    const handleSigningConflictNo = () => {
        setSigningConflict(null);
        signingConflictPendingRef.current = null;
        signingConflictResolveRef.current?.();
        signingConflictResolveRef.current = null;
    };

    const handleSigningConflictYes = async () => {
        const pending = signingConflictPendingRef.current;
        const resolve = signingConflictResolveRef.current;
        setSigningConflict(null);
        signingConflictPendingRef.current = null;
        signingConflictResolveRef.current = null;
        if (!pending?.method || !row?.quoteIdRaw) {
            resolve?.();
            return;
        }
        const m = pending.method;
        setSigningDetails([]);
        setLoading(true);
        try {
            await callViewTrinityContract(m);
        } catch (e: unknown) {
            message.error(
                contractApiErrorMessage(e) ?? (e instanceof Error ? e.message : 'Signing failed.'),
            );
        } finally {
            setLoading(false);
            resolve?.();
        }
    };

    const runSigningAfterRecords = async (method: 'inperson' | 'email') => {
        let list: any[] = [];
        try {
            const records = await contractService.getSigningRecords(row!.quoteIdRaw, method);
            list = normalizeSigningRecordsList(records);
        } catch {
            return;
        }

        if (list.length > 0) {
            let signingCompleted = true;
            for (const r of list) {
                if (r.Signer_Status !== 'COMPLETED') signingCompleted = false;
            }
            const signingPrefix = signingCompleted
                ? 'Combined pack documents signing process completed.'
                : 'Combined pack documents are currently under signing process.';
            const signingRecordBody = `${signingPrefix}\nStarting a new process will cancel the older request.\nPlease click Yes to cancel the current signing process or No to cancel the new request.`;

            const payload = { signingCompleted, body: signingRecordBody, method };
            signingConflictPendingRef.current = { method };
            await new Promise<void>((resolve) => {
                signingConflictResolveRef.current = resolve;
                setSigningConflict(payload);
            });
            return;
        }

        setLoading(true);
        try {
            await callViewTrinityContract(method);
        } catch (e: unknown) {
            message.error(
                contractApiErrorMessage(e) ?? (e instanceof Error ? e.message : 'Signing failed.'),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicateInPersonCancel = () => {
        setDuplicateInPersonOpen(false);
    };

    /**
     * Angular `nzOnOk` on duplicate-email confirm → `GetSigningRecords(type)` only (`generate-contract.service.ts`
     * `GetSigningRecords?financePartnerQuoteId=…&signingMethod=inperson`). No second `CheckDuplicateEmails`.
     */
    const handleDuplicateInPersonOk = () => {
        setDuplicateInPersonOpen(false);
        queueMicrotask(() => {
            void runSigningAfterRecords('inperson').catch((e: unknown) => {
                message.error(
                    contractApiErrorMessage(e) ??
                        (e instanceof Error ? e.message : 'In-person signing failed.'),
                );
                setLoading(false);
            });
        });
    };

    /** Angular `signTrinityContract('inperson')` — clears `documentsUrl` first, then `CheckDuplicateEmails` → confirm → `GetSigningRecords`. */
    const onInPerson = async () => {
        if (!row?.quoteIdRaw) {
            message.warning('Need quote id.');
            return;
        }
        setDocumentsUrl('');
        const dupCheckId = resolveTrinityOpportunityIdForApis(opportunityId, opportunityDetails);
        let dup = false;
        try {
            const res = await contractService.checkDuplicateEmails(dupCheckId);
            dup = isCheckDuplicateEmailsTruthyLikeAngular(res);
        } catch {
            return;
        }

        if (dup) {
            setDuplicateInPersonOpen(true);
            return;
        }
        await runSigningAfterRecords('inperson');
    };

    const onEmailSign = async () => {
        await runSigningAfterRecords('email');
    };

    const toggleCombinedPack = async (e: React.MouseEvent) => {
        const next = !combinedExpanded;
        setCombinedExpanded(next);
        if (next && packTemplates.length === 0 && sf && row?.quoteIdRaw) {
            setTemplatesLoading(true);
            try {
                const res = await quoteServices.retrieveCombinedPackTemplates({
                    salesforceId: sf,
                    financePartnerQuoteId: row.quoteIdRaw,
                    agreementType: 'Combined Pack 1',
                });
                const list = Array.isArray(res) ? res : [];
                setPackTemplates(
                    list.map((label: string, index: number) => ({
                        label: String(label),
                        checked: true,
                        disabled: index === 0,
                    })),
                );
            } catch {
                setPackTemplates([]);
            } finally {
                setTemplatesLoading(false);
            }
        }
        if (!next && packTemplates.length > 0) {
            setPackTemplates((prev) => prev.map((p) => ({ ...p, checked: true })));
        }
        setTimeout(() => (e.target as HTMLElement)?.closest?.('.obd-vtc-modal')?.scrollIntoView?.({ block: 'nearest' }), 0);
    };

    const showHistory = async () => {
        if (!sf || !row?.quoteIdRaw) return;
        setLoading(true);
        try {
            const res = await quoteServices.getCombinedPackHistory({
                salesforceId: sf,
                financePartnerQuoteId: row.quoteIdRaw,
            });
            // Handle common Trinity API response wrappers
            const raw = res as { Message?: string; Data?: unknown; data?: unknown; history?: unknown } | unknown[] | null;
            if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
                const msg = String((raw as { Message?: string }).Message ?? '').trim().toLowerCase();
                if (msg === 'no records found') {
                    setHistoryData([]);
                    setHistoryOpen(true);
                    return;
                }
            }
            const logs = Array.isArray(res) ? res : ((res as { Data?: unknown })?.Data ?? (res as { data?: unknown })?.data ?? (res as { history?: unknown })?.history ?? []);
            setHistoryData(Array.isArray(logs) ? logs : []);
            setHistoryOpen(true);
        } catch (e: unknown) {
            const apiMsg = contractApiErrorMessage(e);
            if (apiMsg && apiMsg.toLowerCase() === 'no records found') {
                setHistoryData([]);
                setHistoryOpen(true);
                return;
            }
            message.error(apiMsg ?? (e instanceof Error ? e.message : 'Failed to load history.'));
        } finally {
            setLoading(false);
        }
    };

    const updateAgreementRefresh = async (agreementId: string) => {
        setLoading(true);
        try {
            await quoteServices.updateAgreementSignDetails(agreementId);
            await loadSigningDetails(agreementId);
        } catch (e: unknown) {
            message.error(contractApiErrorMessage(e) ?? 'Refresh failed.');
        } finally {
            setLoading(false);
        }
    };

    if (!open || !row) return null;

    if (mode === 'proposal') {
        const proposalTitle = getViewTrinityProposalModalTitle(row.productTypeCode || row.productType);
        return (
            <DashboardModal
                title={proposalTitle}
                open={open}
                onClose={onClose}
                width={620}
                footer={null}
                centered
                zIndex={11000}
                theme={theme}
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
                className="obd-tpm-modal obd-vtc-modal"
            >
                <TrinityProposalModal
                    row={row}
                    opportunityId={opportunityId}
                    onAfterAction={onAfterAction}
                    onClose={handleClose}
                    theme={theme}
                    isGenerate={isGenerate}
                />
            </DashboardModal>
        );
    }

    return (
        <>
        <DashboardModal
            title="View Trinity Contract"
            open={open}
            onClose={handleClose}
            width={980}
            centered
            /** Above Agreements modal (10060) and other oneBUTTON overlays — render on `body` so nested modals are not clipped. */
            zIndex={11000}
            theme={theme}
            maskClosable={false}
            destroyOnClose
            rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
            className="obd-vtc-modal"
            footer={
                <div className="obd-vtc-modal__footer">
                    <Button type="primary" icon={<DownloadSimple size={16} />} onClick={() => void onDownload()} disabled={modalBusy}>
                        Download
                    </Button>
                    <Button
                        type="primary"
                        className="obd-vtc-modal__btn-success"
                        icon={<PencilSimple size={16} />}
                        onClick={() =>
                            void onInPerson().catch((e: unknown) =>
                                message.error(
                                    contractApiErrorMessage(e) ??
                                        (e instanceof Error ? e.message : 'In-Person Signing failed.'),
                                ),
                            )
                        }
                        disabled={modalBusy}
                    >
                        In-Person Signing
                    </Button>
                    <Button
                        type="primary"
                        className="obd-vtc-modal__btn-success"
                        icon={<PencilSimple size={16} />}
                        onClick={() =>
                            void onEmailSign().catch((e: unknown) =>
                                message.error(
                                    contractApiErrorMessage(e) ??
                                        (e instanceof Error ? e.message : 'Send for Signature failed.'),
                                ),
                            )
                        }
                        disabled={modalBusy}
                    >
                        Send for Signature
                    </Button>
                </div>
            }
        >
            <div className="obd-vtc-modal__inner obd-vtc-modal__shell" style={{ position: 'relative' }}>
                <div className="obd-vtc-modal__grid">
                <div className="obd-vtc-modal__col obd-vtc-modal__col--left">
                    <div className="obd-vtc-modal__picker-head">
                        <b>Document Picker</b>
                        <Tooltip title="Combined Pack History">
                            <button type="button" className="obd-vtc-modal__history-btn" onClick={() => void showHistory()}>
                                <FileText size={18} weight="bold" aria-hidden />
                            </button>
                        </Tooltip>
                    </div>

                <div className="obd-vtc-modal__checks">
                    {visibleControls.map((c) => (
                        <div key={c.name} className="obd-vtc-modal__check-row">
                            <div className="obd-vtc-modal__check-main">
                                <Checkbox
                                    checked={!!checks[c.name]}
                                    disabled={c.name === 'CombinedPack'}
                                    onChange={(e) => setChecks((prev) => ({ ...prev, [c.name]: e.target.checked }))}
                                >
                                    <span className={c.name === 'CombinedPack' ? 'obd-vtc-modal__label-strong' : undefined}>{c.label}</span>
                                </Checkbox>
                                {c.name === 'CombinedPack' ? (
                                    <button
                                        type="button"
                                        className="obd-vtc-modal__expand"
                                        onClick={(e) => void toggleCombinedPack(e)}
                                        disabled={templatesLoading}
                                        aria-busy={templatesLoading}
                                    >
                                        {combinedExpanded ? (
                                            <MinusCircle size={20} weight="duotone" aria-hidden />
                                        ) : (
                                            <PlusCircle size={20} weight="duotone" aria-hidden />
                                        )}
                                    </button>
                                ) : null}
                            </div>
                            {c.name === 'CombinedPack' ? (
                                <div className={`obd-vtc-modal__templates${combinedExpanded ? ' obd-vtc-modal__templates--open' : ''}`}>
                                    {!templatesLoading && packTemplates.length > 0 ? (
                                        packTemplates.map((doc) => (
                                            <label key={doc.label} className="obd-vtc-modal__tpl-row">
                                                <Checkbox
                                                    checked={doc.checked}
                                                    disabled={doc.disabled}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setPackTemplates((prev) =>
                                                            prev.map((p) => (p.label === doc.label ? { ...p, checked } : p)),
                                                        );
                                                    }}
                                                />
                                                <span className={doc.disabled ? 'obd-vtc-modal__muted' : undefined}>{doc.label}</span>
                                            </label>
                                        ))
                                    ) : !templatesLoading && combinedExpanded ? (
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No templates" />
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
                </div>

                <div className="obd-vtc-modal__col obd-vtc-modal__col--right">
                {signingDetails.length > 0 ? (
                    <div className="obd-vtc-modal__signing">
                        <b>
                            Signing Status — {signType === 'inperson' ? 'In-Person' : 'Email'}
                            {!isSigned && signType === 'inperson' ? ' — Refresh status via reload icon if needed' : ''}
                            {!isSigned && signType === 'email' ? ' — Status of each signee below' : ''}
                        </b>
                        {signingDetails.map((sign: any) => {
                            const nameCat = `${sign.Contact_Name ?? ''} — ${sign.Signer_Category ?? ''}`;
                            const signedBadge =
                                sign.Signer_Status === 'WAITING_FOR_OTHERS' || sign.Is_Signed;
                            const stLo = String(sign.Signer_Status ?? '').toLowerCase();
                            const awaitingLine =
                                signType === 'email' && (!!sign.LastEventDate || stLo === 'sent');
                            const emailLabeledRow = (sign.Is_Signed || sign.Signer_URL) && signType === 'email';
                            return (
                                <div key={`${sign.Agreement_Id}-${sign.Signer_Order}`} className="obd-vtc-modal__sign-row">
                                    {sign.reloader ? (
                                        <button
                                            type="button"
                                            className="obd-vtc-modal__reload"
                                            onClick={() => void updateAgreementRefresh(String(sign.Agreement_Id))}
                                            aria-label="Refresh signing status"
                                        >
                                            ↻
                                        </button>
                                    ) : null}
                                    <span
                                        className={
                                            sign.reloader
                                                ? 'obd-vtc-modal__sign-row-text'
                                                : 'obd-vtc-modal__sign-row-text obd-vtc-modal__sign-row-text--indented'
                                        }
                                    >
                                        {!sign.Is_Signed && !sign.Signer_URL ? <span>{nameCat}</span> : null}
                                        {sign.Signer_URL && signType === 'inperson' ? (
                                            <a href={sign.Signer_URL} target="_blank" rel="noopener noreferrer">
                                                {nameCat}
                                            </a>
                                        ) : null}
                                        {emailLabeledRow ? (
                                            <button
                                                type="button"
                                                className="obd-vtc-modal__email-signee"
                                                onClick={() =>
                                                    checkEmailBox(
                                                        sign.Email,
                                                        sign.Signer_Status === 'WAITING_FOR_OTHERS' || !!sign.Is_Signed,
                                                    )
                                                }
                                            >
                                                <span className="obd-vtc-modal__email-signee-name">{nameCat}</span>
                                                {awaitingLine ? (
                                                    <span className="obd-vtc-modal__email-awaiting">
                                                        {' '}
                                                        (Awaiting Signature - Email Sent
                                                        {sign.LastEventDate ? ` — [${sign.LastEventDate}]` : ''})
                                                    </span>
                                                ) : null}
                                            </button>
                                        ) : null}
                                        {signedBadge ? <b className="obd-vtc-modal__signed"> (Signed)</b> : null}
                                    </span>
                                </div>
                            );
                        })}
                        {isSigned ? (
                            <div className="obd-vtc-modal__signed-banner">
                                <CheckCircle size={16} weight="bold" aria-hidden />
                                Combined Pack has been Signed
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {documentsUrl ? (
                    <div className="obd-vtc-modal__link-wrap">
                        <a href={documentsUrl} target="_blank" rel="noopener noreferrer">
                            Click here for Combined Pack
                        </a>
                    </div>
                ) : null}
                </div>
                </div>
                {modalBusy ? (
                    <div
                        className="obd-vtc-modal__full-loader"
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <Spin size="default" className="obd-vtc-modal__loader-spin" />
                        <p className="obd-vtc-modal__loader-text">
                            {templatesLoading
                                ? 'Loading combined pack templates…'
                                : 'Please wait…'}
                        </p>
                    </div>
                ) : null}
                </div>
        </DashboardModal>

        <DashboardModal
            title="Confirm In-Person Signing"
            open={duplicateInPersonOpen}
            onClose={handleDuplicateInPersonCancel}
            theme={theme}
            zIndex={DUPLICATE_IN_PERSON_MODAL_Z}
            getContainer={() => document.body}
            centered
            maskClosable={false}
            destroyOnClose
            rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
            className="duplicate-email-modal obd-vtc-modal obd-vtc-modal--dup-inperson"
            width={555}
            footer={[
                <Button
                    key="cancel"
                    className="obd-vtc-modal__dialog-btn obd-vtc-modal__dialog-btn--cancel"
                    icon={<XCircle size={16} weight="bold" />}
                    onClick={handleDuplicateInPersonCancel}
                >
                    Cancel
                </Button>,
                <Button
                    key="ok"
                    type="primary"
                    className="obd-vtc-modal__dialog-btn obd-vtc-modal__dialog-btn--primary"
                    icon={<CheckCircle size={16} weight="bold" />}
                    onClick={handleDuplicateInPersonOk}
                >
                    OK
                </Button>,
            ]}
        >
            <div className="obd-vtc-modal__dup-inperson-body">{DUPLICATE_IN_PERSON_EMAIL_BODY}</div>
        </DashboardModal>

        <DashboardModal
            title={
                signingConflict?.signingCompleted ? 'Signing completed' : 'Signing in progress'
            }
            open={!!signingConflict}
            onClose={handleSigningConflictNo}
            theme={theme}
            zIndex={SIGNING_FLOW_OVERLAY_Z}
            getContainer={() => document.body}
            centered
            maskClosable={false}
            destroyOnClose
            rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
            className="obd-vtc-modal obd-vtc-modal--signing-conflict"
            width={520}
            footer={[
                <Button
                    key="no"
                    className="obd-vtc-modal__dialog-btn obd-vtc-modal__dialog-btn--cancel"
                    icon={<XCircle size={16} weight="bold" />}
                    onClick={handleSigningConflictNo}
                >
                    No
                </Button>,
                <Button
                    key="yes"
                    type="primary"
                    className="obd-vtc-modal__dialog-btn obd-vtc-modal__dialog-btn--primary"
                    icon={<CheckCircle size={16} weight="bold" />}
                    onClick={() => void handleSigningConflictYes()}
                >
                    Yes
                </Button>,
            ]}
        >
            {signingConflict ? (
                <div className="obd-vtc-modal__signing-conflict-body">{signingConflict.body}</div>
            ) : null}
        </DashboardModal>

        <DashboardModal
            title="Combined Pack History"
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            width={750}
            zIndex={NESTED_MODAL_Z_INDEX}
            centered
            theme={theme}
            rootClassName={`obd-sync-modal-root obd-theme-${theme}`}
            className="obd-vtc-modal"
            footer={[
                <Button key="close" type="primary" onClick={() => setHistoryOpen(false)}>
                    Close
                </Button>
            ]}
        >
            <div className="obd-vtc-modal__history-body">
                <Table 
                    size="small"
                    pagination={false}
                    dataSource={Array.isArray(historyData) ? historyData : []}
                    rowKey={(record, idx) => idx}
                    columns={[
                        {
                            title: 'User',
                            key: 'user',
                            width: 140,
                            render: (_, record) => (
                                <span>{record.User || record.User_Name || record.Created_By_Name || 'System'}</span>
                            )
                        },
                        {
                            title: 'Templates',
                            key: 'templates',
                            render: (_, record) => {
                                const templates = record.Templates || record.Template_Names || record.Action_Description || record.Comments;
                                if (Array.isArray(templates) && templates.length > 0) {
                                    return (
                                        <ul className="obd-vtc-modal__history-ul">
                                            {templates.map((t: string, i: number) => <li key={i}>{t}</li>)}
                                        </ul>
                                    );
                                }
                                if (typeof templates === 'string' && templates.trim() !== '') {
                                    if (templates.includes('.pdf') && (templates.includes(',') || templates.includes(';'))) {
                                        const list = templates.split(/[,;]/).map(s => s.trim()).filter(Boolean);
                                        return (
                                            <ul className="obd-vtc-modal__history-ul">
                                                {list.map((t, i) => <li key={i}>{t}</li>)}
                                            </ul>
                                        );
                                    }
                                    return <span>{templates}</span>;
                                }
                                return <span>Combined Pack generated with all default templates selected</span>;
                            }
                        },
                        {
                            title: 'Date',
                            key: 'date',
                            width: 190,
                            render: (_, record) => {
                                const d = record.Date || record.Created_Date;
                                return (
                                    <span>
                                        {d ? new Date(d).toLocaleString('en-US', {
                                            month: '2-digit',
                                            day: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: true
                                        }) : '—'}
                                    </span>
                                );
                            }
                        }
                    ]}
                    locale={{ emptyText: <Empty description="No history records found" /> }}
                />
            </div>
        </DashboardModal>
    </>
    );
}
